import React, { useState, useCallback, useMemo } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  TimePicker,
  Switch,
  Slider,
  ColorPicker,
  Space,
} from "antd";
import type { Rule } from "antd/es/form";
import type { Color } from "antd/es/color-picker";
import { useTranslation } from "react-i18next";
import type { DynamicFormFieldProps } from "./types";
import {
  RelatedItemCreateModal,
  getResourceFromItemType,
} from "./RelatedItemCreateModal";
import { DynamicFormFieldDisplay } from "./DynamicFormFieldDisplay";
import { FileUploadField } from "./FileUploadField";
import ItemReferenceSelect from "./ItemReferenceSelect";
import { useFormItemType } from "./FormItemTypeContext";
import { getFieldTooltip } from "../../config/fieldTooltips";
import { useTocLabels } from "../../contexts/TocLabelContext";

const { TextArea } = Input;

/**
 * Build validation rules from field metadata
 */
const buildValidationRules = (
  field: DynamicFormFieldProps["field"],
  t: (key: string, options?: Record<string, unknown>) => string
): Rule[] => {
  const rules: Rule[] = [];

  // Required field
  if (field.isRequired) {
    rules.push({
      required: true,
      message: t("dynamicForm.fieldRequired", {
        field: field.label || field.mappedName,
      }),
    });
  }

  // Max length for string fields
  if (
    field.maxLength &&
    (field.dataType === "string" || field.dataType === "text")
  ) {
    rules.push({
      max: field.maxLength,
      message: t("dynamicForm.maxLength", { max: field.maxLength }),
    });
  }

  // Pattern validation (skip for date fields - DatePicker handles its own format)
  if (field.pattern && field.dataType !== "date") {
    rules.push({
      pattern: new RegExp(field.pattern),
      message: t("dynamicForm.invalidFormat"),
    });
  }

  // Email validation
  if (
    field.dataType === "string" &&
    (field.mappedName.toLowerCase().includes("email") ||
      field.name.toLowerCase().includes("email"))
  ) {
    rules.push({
      type: "email",
      message: t("dynamicForm.invalidEmail"),
    });
  }

  // URL validation
  if (
    field.dataType === "string" &&
    (field.mappedName.toLowerCase().includes("website") ||
      field.mappedName.toLowerCase().includes("url"))
  ) {
    rules.push({
      type: "url",
      message: t("dynamicForm.invalidUrl"),
    });
  }

  return rules;
};

/**
 * Translate ItemType name for UI display
 * Uses hybrid approach: translation → TOC label → original name
 */
const translateItemType = (
  itemType: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  getLabel?: (itemTypeName: string, language: string) => string | undefined
): string => {
  if (!itemType) return "";
  // Try to get translation from navigation namespace
  const translationKey = `navigation.${itemType}`;
  const translated = t(translationKey);
  // If translation key is found (not returned as-is), use it
  if (translated !== translationKey) {
    return translated;
  }
  // Fall back to TOC menu label from Aras
  if (getLabel) {
    const tocLabel = getLabel(itemType, language);
    if (tocLabel) {
      return tocLabel;
    }
  }
  // Final fallback: use original name
  return itemType;
};

/**
 * Custom option render for color list - shows color chip next to label
 */
const renderColorOption = (option: { label: string; value: string }) => (
  <Space>
    <span
      style={{
        display: "inline-block",
        width: 16,
        height: 16,
        backgroundColor: option.value,
        borderRadius: 2,
        border: "1px solid #d9d9d9",
      }}
    />
    {option.label}
  </Space>
);

/**
 * Props for renderFieldInput - item reference fields need extra handling
 */
interface RenderFieldInputOptions {
  field: DynamicFormFieldProps["field"];
  disabled: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  language: string;
  getLabel?: (itemTypeName: string, language: string) => string | undefined;
  showAddNew?: boolean;
  onOpenAddNewModal?: () => void;
}

/**
 * Render form input based on data type
 * Supports all Aras Innovator data types:
 * - string, text: Text inputs
 * - integer, decimal, float: Number inputs
 * - date: Date picker
 * - boolean: Switch toggle
 * - list: Single-select dropdown
 * - color: Color picker
 * - color list: Dropdown with color preview chips
 * - item: Reference to another item (searchable select)
 * - filter list: Single-select filtered dropdown
 * - mv_list: Multi-select dropdown
 * - md5: Password input
 */
const renderFieldInput = ({
  field,
  disabled,
  t,
  language,
  getLabel,
  showAddNew,
  onOpenAddNewModal,
}: RenderFieldInputOptions): React.ReactNode => {
  const { dataType, options, mappedName } = field;

  // Currency fields get special formatting
  const isCurrencyField =
    mappedName === "estimatedValue" ||
    mappedName === "amount" ||
    mappedName === "totalRevenue" ||
    mappedName === "annualRevenue";

  // Probability field gets slider
  const isProbabilityField = mappedName === "probability";

  // Time-only fields (ERP_Attendance: Giờ làm, Giờ làm thêm)
  // These fields store time duration, not datetime
  const isTimeField =
    mappedName === "workingTime" ||
    mappedName === "overtime" ||
    mappedName === "working_time" ||
    mappedName === "over_time";

  // Normalize data type (Aras uses spaces, we use lowercase)
  const normalizedType = dataType.toLowerCase().replace(/\s+/g, "_");

  // Special handling for time fields regardless of their Aras dataType
  if (isTimeField) {
    return (
      <TimePicker
        style={{ width: "100%" }}
        disabled={disabled}
        placeholder={t("dynamicForm.selectTime")}
        format="HH:mm"
        showNow
      />
    );
  }

  switch (normalizedType) {
    case "string":
      return (
        <Input
          disabled={disabled}
          placeholder={
            field.label
              ? t("dynamicForm.enter", { field: field.label })
              : undefined
          }
        />
      );

    case "text":
      return (
        <TextArea
          rows={3}
          disabled={disabled}
          placeholder={
            field.label
              ? t("dynamicForm.enter", { field: field.label })
              : undefined
          }
        />
      );

    case "integer":
      if (isProbabilityField) {
        return (
          <Slider
            disabled={disabled}
            min={0}
            max={100}
            marks={{ 0: "0%", 25: "25%", 50: "50%", 75: "75%", 100: "100%" }}
          />
        );
      }
      return (
        <InputNumber
          style={{ width: "100%" }}
          precision={0}
          disabled={disabled}
        />
      );

    case "decimal":
    case "float":
      if (isCurrencyField) {
        return (
          <InputNumber
            style={{ width: "100%" }}
            disabled={disabled}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) =>
              value!.replace(/\$\s?|(,*)/g, "") as unknown as number
            }
          />
        );
      }
      return <InputNumber style={{ width: "100%" }} disabled={disabled} />;

    case "date":
      return (
        <DatePicker
          style={{ width: "100%" }}
          disabled={disabled}
          placeholder={t("dynamicForm.selectDate")}
          format="DD/MM/YYYY"
        />
      );

    case "datetime":
      return (
        <DatePicker
          style={{ width: "100%" }}
          disabled={disabled}
          placeholder={t("dynamicForm.selectDateTime")}
          format="DD/MM/YYYY HH:mm"
          showTime={{ format: "HH:mm" }}
        />
      );

    case "time":
      return (
        <TimePicker
          style={{ width: "100%" }}
          disabled={disabled}
          placeholder={t("dynamicForm.selectTime")}
          format="HH:mm"
          showNow
        />
      );

    case "boolean":
      return <Switch disabled={disabled} />;

    case "list":
    case "filter_list":
      return (
        <Select
          options={options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
          disabled={disabled}
          placeholder={t("dynamicForm.select", { field: field.label })}
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString() ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      );

    // Color picker - stores hex color value
    case "color":
      return (
        <ColorPicker
          disabled={disabled}
          showText
          format="hex"
          onChange={(_color: Color, hex: string) => hex}
        />
      );

    // Color list - dropdown with color preview chips
    case "color_list":
      return (
        <Select
          options={options?.map((opt) => ({
            label: renderColorOption(opt),
            value: opt.value,
            searchLabel: opt.label, // For search filtering
          }))}
          disabled={disabled}
          placeholder={t("dynamicForm.select", { field: field.label })}
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.searchLabel?.toString() ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          optionRender={(option) =>
            renderColorOption({
              label: option.data.searchLabel || option.label?.toString() || "",
              value: option.value?.toString() || "",
            })
          }
        />
      );

    // Item reference - searchable select with server-side search
    // dataSource contains the related ItemType name
    case "item":
      // Site field uses a simple Select with pre-loaded options (no search/add-new)
      if (field.isSiteField) {
        return (
          <Select
            options={options?.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }))}
            disabled={disabled}
            placeholder={t("dynamicForm.selectItem", {
              field: field.label,
              itemType: field.label || "",
            })}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        );
      }
      return (
        <ItemReferenceSelect
          options={options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
            description: opt.description || "",
          }))}
          dataSource={field.dataSource}
          disabled={disabled}
          placeholder={t("dynamicForm.selectItem", {
            field: field.label,
            itemType: translateItemType(
              field.dataSource,
              t,
              language,
              getLabel
            ),
          })}
          notFoundContent={t("dynamicForm.noItemsFound")}
          showAddNew={showAddNew}
          addNewLabel={t("dynamicForm.addNew", {
            itemType: translateItemType(
              field.dataSource,
              t,
              language,
              getLabel
            ),
          })}
          onOpenAddNewModal={onOpenAddNewModal}
        />
      );

    // Multi-value list - multiple selection with checkboxes
    // Values stored as comma-separated string in Aras
    case "mv_list":
    case "multi_value_list":
      return (
        <Select
          mode="multiple"
          options={options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
          disabled={disabled}
          placeholder={t("dynamicForm.selectMultiple", { field: field.label })}
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString() ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          // Convert from comma-separated to array on display
          // and back to comma-separated on change
        />
      );

    // MD5 - password/hash field
    case "md5":
      return (
        <Input.Password
          disabled={disabled}
          placeholder={
            field.label
              ? t("dynamicForm.enter", { field: field.label })
              : undefined
          }
        />
      );

    // Image - file upload or URL input (simplified as URL for now)
    case "image":
      return (
        <Input
          disabled={disabled}
          placeholder={t("dynamicForm.enterImageUrl")}
          addonBefore={
            <span
              style={{
                display: "inline-block",
                width: 16,
                height: 16,
              }}
            >
              🖼️
            </span>
          }
        />
      );

    // Sequence - auto-generated, read-only
    case "sequence":
      return (
        <Input
          disabled
          placeholder={t("dynamicForm.autoGenerated")}
          style={{ backgroundColor: "#f5f5f5" }}
        />
      );

    default:
      // Fallback to text input for unknown types
      return (
        <Input
          disabled={disabled}
          placeholder={
            field.label
              ? t("dynamicForm.enter", { field: field.label })
              : undefined
          }
        />
      );
  }
};

/**
 * Dynamic form field component that renders based on metadata
 */
export const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  form,
  disabled = false,
  readOnly = false,
  showAddNew = true,
  onItemCreated,
}) => {
  const { t, i18n } = useTranslation();
  const { getLabel } = useTocLabels();
  const itemTypeName = useFormItemType();
  const [modalOpen, setModalOpen] = useState(false);

  const tooltip = getFieldTooltip(field.mappedName, itemTypeName, i18n.language);
  const rules = buildValidationRules(field, t);
  const isBoolean = field.dataType === "boolean";
  const normalizedDataType = field.dataType.toLowerCase().replace(/\s+/g, "_");
  const isItemRef = normalizedDataType === "item";

  // Check if field is a list-based select (needs value-to-string normalization)
  const isListBased =
    normalizedDataType === "list" ||
    normalizedDataType === "filter_list" ||
    normalizedDataType === "mv_list" ||
    normalizedDataType === "multi_value_list" ||
    normalizedDataType === "color_list";

  // Disable "Add New" for File type items - File requires actual file upload,
  // cannot be created via simple form submission
  const isFileType = field.dataSource?.toLowerCase() === "file";

  // Get API-provided display name for item reference fields
  // The API returns {field}Display (e.g., customerDisplay) with keyed_name
  const itemDisplayValue = isItemRef
    ? (form.getFieldValue(field.mappedName + "Display") as string)
    : undefined;

  // For item reference fields, ensure the current value has a readable label
  // If the current value isn't in the options, inject a synthetic option
  const enrichedField = useMemo(() => {
    if (!isItemRef || !field.options) return field;

    const currentValue = form.getFieldValue(field.mappedName);
    if (!currentValue) return field;

    // Check if current value already has a matching option
    const hasMatch = field.options.some((opt) => opt.value === currentValue);
    if (hasMatch) return field;

    // Use display value from the API response
    if (!itemDisplayValue) return field;

    // Inject synthetic option so the Select shows the display name
    return {
      ...field,
      options: [
        { label: itemDisplayValue, value: currentValue },
        ...field.options,
      ],
    };
  }, [field, form, isItemRef, itemDisplayValue]);

  // Read-only mode: render display value instead of form input
  if (readOnly) {
    const value = form.getFieldValue(field.mappedName);
    const labelText = t(
      ["forms." + field.mappedName, "table." + field.mappedName],
      field.label || field.mappedName
    );
    return (
      <Form.Item
        label={
          <span
            style={{
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.65)",
              fontSize: 13,
            }}
          >
            {labelText}
          </span>
        }
        data-testid={`field-${field.mappedName}`}
        style={{ marginBottom: 16 }}
        className="readonly-field"
      >
        <DynamicFormFieldDisplay
          field={field}
          value={value}
          displayValue={itemDisplayValue}
        />
      </Form.Item>
    );
  }

  // Handle modal open
  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Handle new item created - set form value and trigger callback
  const handleItemCreated = useCallback(
    (newItem: { id: string; name?: string }) => {
      // Set the form field value to the new item's ID
      form.setFieldValue(field.mappedName, newItem.id);

      // Call parent callback if provided
      if (onItemCreated && field.dataSource) {
        onItemCreated(field.dataSource, newItem);
      }
    },
    [form, field.mappedName, field.dataSource, onItemCreated]
  );

  // For File type fields, render FileUploadField instead of Select dropdown
  if (isFileType && isItemRef) {
    return (
      <Form.Item
        label={t(
          ["forms." + field.mappedName, "table." + field.mappedName],
          field.label || field.mappedName
        )}
        name={field.mappedName}
        rules={rules}
        tooltip={tooltip}
        data-testid={`field-${field.mappedName}`}
      >
        <FileUploadField disabled={disabled} />
      </Form.Item>
    );
  }

  return (
    <>
      <Form.Item
        label={t(
          ["forms." + field.mappedName, "table." + field.mappedName],
          field.label || field.mappedName
        )}
        name={field.mappedName}
        rules={rules}
        tooltip={tooltip}
        valuePropName={isBoolean ? "checked" : "value"}
        data-testid={`field-${field.mappedName}`}
        // For list-based selects, normalize values to strings so Ant Design Select
        // can match option values (API may return numbers, options are strings)
        getValueProps={
          isListBased
            ? (value) => ({
                value:
                  value != null && !Array.isArray(value)
                    ? String(value)
                    : value,
              })
            : undefined
        }
        normalize={
          isListBased
            ? (value) =>
                value != null && !Array.isArray(value)
                  ? String(value)
                  : value
            : undefined
        }
      >
        {renderFieldInput({
          field: enrichedField,
          disabled,
          t,
          language: i18n.language,
          getLabel,
          showAddNew: showAddNew && isItemRef && !!field.dataSource,
          onOpenAddNewModal: handleOpenModal,
        })}
      </Form.Item>

      {/* Modal for creating new related items (not for File type) */}
      {isItemRef && field.dataSource && !isFileType && (
        <RelatedItemCreateModal
          itemTypeName={field.dataSource}
          resourceName={getResourceFromItemType(field.dataSource)}
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleItemCreated}
        />
      )}
    </>
  );
};
